import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuthContext } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Mail, Lock, Eye, EyeOff, Heart, Loader2, Phone, ArrowLeft } from "lucide-react";
import { authFormSchema, safeValidate } from "@/lib/validations";

const AuthPage = () => {
  const [searchParams] = useSearchParams();
  const method = searchParams.get("method");
  const signinParam = searchParams.get("signin");
  const [isLogin, setIsLogin] = useState(signinParam === "true");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const { signIn, signUp } = useAuthContext();
  const navigate = useNavigate();

  const validateForm = (): boolean => {
    const validation = safeValidate(authFormSchema, { email, password });
    
    if (validation.success === false) {
      const errorMsg = validation.error;
      if (errorMsg.toLowerCase().includes("email")) {
        setErrors({ email: errorMsg });
      } else if (errorMsg.toLowerCase().includes("password")) {
        setErrors({ password: errorMsg });
      } else {
        toast({ title: "Validation Error", description: errorMsg, variant: "destructive" });
      }
      return false;
    }
    
    setErrors({});
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      if (isLogin) {
        const { error } = await signIn(email.trim(), password);
        if (error) {
          toast({ title: "Error", description: error.message, variant: "destructive" });
        } else {
          toast({ title: "Welcome back!", description: "Redirecting..." });
          // Navigate happens automatically via AuthContext + route protection
        }
      } else {
        const { data, error } = await signUp(email.trim(), password);
        if (error) {
          toast({ title: "Error", description: error.message, variant: "destructive" });
        } else if (data?.user) {
          toast({ title: "Account created!", description: "Let's set up your profile" });
          // New users go to onboarding (handled by OnboardingGate in routes)
          navigate("/onboarding");
        }
      }
    } catch (err) {
      toast({ 
        title: "Error", 
        description: err instanceof Error ? err.message : "Something went wrong", 
        variant: "destructive" 
      });
    } finally {
      setLoading(false);
    }
  };

  // If phone method, show phone auth (placeholder for now)
  if (method === "phone") {
    return (
      <div className="min-h-screen sky-gradient flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <button
            onClick={() => navigate("/")}
            className="mb-6 flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <div className="bg-card rounded-3xl shadow-balloon p-8">
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-[hsl(var(--romantic-primary))] to-[hsl(var(--romantic-accent))] flex items-center justify-center shadow-[0_10px_40px_-10px_hsl(var(--romantic-primary)/0.25%)]">
                <Phone className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">Continue with Phone</h2>
              <p className="text-muted-foreground">Enter your phone number to get started</p>
            </div>
            <div className="space-y-4">
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="tel"
                  placeholder="Phone number"
                  className="w-full pl-12 pr-4 py-3 rounded-xl bg-muted border-0 focus:ring-2 focus:ring-[hsl(var(--romantic-primary))] outline-none"
                />
              </div>
              <button
                className="w-full py-4 rounded-xl bg-gradient-to-r from-[hsl(var(--romantic-primary))] to-[hsl(var(--romantic-accent))] text-white font-semibold shadow-[0_10px_40px_-10px_hsl(var(--romantic-primary)/0.25%)] hover:shadow-[0_15px_50px_-10px_hsl(var(--romantic-primary)/0.35%)] hover:scale-[1.02] active:scale-[0.98] transition-all"
              >
                Send Verification Code
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen sky-gradient flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <button
          onClick={() => navigate("/")}
          className="mb-6 flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back</span>
        </button>
        <div className="text-center mb-8">
          {/* Logo - Always visible */}
          <div className="w-32 h-32 mx-auto mb-1 gradient-soft rounded-2xl p-1.5">
            <img 
              src="/new-logo.png" 
              alt="SpikeBloom Logo" 
              className="w-full h-full object-contain"
              style={{ mixBlendMode: 'multiply' }}
            />
            </div>
          <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: "'Bolder Elephant', cursive" }}>SpikeBloom</h1>
          <p className="text-muted-foreground mt-2">Meet real people. Match instantly.</p>
        </div>

        <div className="bg-card rounded-3xl shadow-balloon p-8">
          <div className="flex mb-6 bg-muted rounded-full p-1">
            <button
              onClick={() => setIsLogin(true)}
              className={`flex-1 py-2 rounded-full font-medium transition-all ${isLogin ? "bg-gradient-to-r from-[hsl(var(--romantic-primary))] to-[hsl(var(--romantic-accent))] text-white" : "text-muted-foreground"}`}
            >
              Sign In
            </button>
            <button
              onClick={() => setIsLogin(false)}
              className={`flex-1 py-2 rounded-full font-medium transition-all ${!isLogin ? "bg-gradient-to-r from-[hsl(var(--romantic-primary))] to-[hsl(var(--romantic-accent))] text-white" : "text-muted-foreground"}`}
            >
              Sign Up
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email address"
                className={`w-full pl-12 pr-4 py-3 rounded-xl bg-muted border-0 focus:ring-2 focus:ring-pop outline-none ${errors.email ? "ring-2 ring-destructive" : ""}`}
                required
              />
              {errors.email && (
                <p className="text-xs text-destructive mt-1 ml-1">{errors.email}</p>
              )}
            </div>

            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                className={`w-full pl-12 pr-12 py-3 rounded-xl bg-muted border-0 focus:ring-2 focus:ring-[hsl(var(--romantic-primary))] outline-none ${errors.password ? "ring-2 ring-destructive" : ""}`}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
              {errors.password && (
                <p className="text-xs text-destructive mt-1 ml-1">{errors.password}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 rounded-xl bg-gradient-to-r from-[hsl(var(--romantic-primary))] to-[hsl(var(--romantic-accent))] text-white font-semibold shadow-[0_10px_40px_-10px_hsl(var(--romantic-primary)/0.25%)] hover:shadow-[0_15px_50px_-10px_hsl(var(--romantic-primary)/0.35%)] hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="w-5 h-5 animate-spin" />}
              {loading ? "Please wait..." : isLogin ? "Sign In" : "Create Account"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
